/* eslint-disable valid-jsdoc */
import _ from 'underscore';
import { Message } from 'botframework-directlinejs';
import { v4 as uuidV4 } from 'uuid';
import { type BotServiceConfig } from '@bingads-webui/ads-copilot-shared-types';
import { IUserMessagePayload } from '../../botTypes';
import {
  EVENT_NAME_ENGAGE_TROUBLESHOOTER,
  CATEGORY_TROUBLESHOOTING,
  TROUBLESHOOTING_AD_DELIVERY,
} from '../../utils';

interface ICreateMessageValueProps {
  locale: string | undefined,
  botServiceConfig: BotServiceConfig,
  isHiddenFromUser?: boolean,
  isTroubleshooting?: boolean,
  options?: object,
  additionalContext?: object,
  pluginName?: string | undefined,
  delayBeforeCallingAIService?: string | undefined, // in milliseconds
  getTailorContext?: () => object,
}

function createMessageValue(props: ICreateMessageValueProps): IUserMessagePayload {
  const {
    locale,
    botServiceConfig,
    isHiddenFromUser,
    isTroubleshooting,
    options,
    additionalContext,
    pluginName,
    delayBeforeCallingAIService,
    getTailorContext,
  } = props;

  const {
    accountId,
    botFlightMode,
    ccSmallToken,
    customerId,
    ianaTimeZone,
    productName,
    userid,
  } = botServiceConfig;

  const v: IUserMessagePayload = {
    QuestionId: uuidV4(),
    Locale: locale?.toLowerCase(),
    Product: productName,
    Timezone: ianaTimeZone,
  };

  if (botFlightMode) {
    v.BotFlightMode = botFlightMode;
  }

  if (pluginName) {
    v.PluginContext = { Name: pluginName };
  }

  // UxContext is used by AI plugins to make any additional requests on behalf of user
  v.UxContext = {
    CustomerId: customerId,
    AccountId: accountId,
    UserId: userid,
    AUTH_CCSmallToken: ccSmallToken,
  };

  if (delayBeforeCallingAIService) {
    v.UxContext.DelayBeforeCallingAIService = delayBeforeCallingAIService;
  }

  if (getTailorContext) {
    v.UxContext.TailorContext = getTailorContext();
  }

  if (isHiddenFromUser) {
    v.isHiddenFromUser = isHiddenFromUser;
  }

  if (isTroubleshooting) {
    v.Features = ['Troubleshooting'];
  }

  if (options) {
    v.options = options;
  }

  if (additionalContext) {
    // AdditionalContext is used to pass additional information to the bot
    // This parameter is consumed by both Bot MT and AI side
    // Today, it contains pilot value only
    v.AdditionalContext = {
      ...additionalContext,
    };
  }

  return v;
}

/**
 * Creates a user Message with the default "from" and "value" metadata.
 */
export function createMessage(
  text: string,
  options: {
    locale: string | undefined,
    botServiceConfig: BotServiceConfig,
    category?: string | undefined,
    isCustomStream?: boolean,
    isHiddenFromUser?: boolean,
    options?: object,
    additionalContext?: object,
    pluginName?: string | undefined,
    delayBeforeCallingAIService?: string | undefined,
    getTailorContext?: () => object,
  }
): Message {
  const {
    locale,
    botServiceConfig,
    category,
    isCustomStream,
    isHiddenFromUser,
    options: additionalOptions,
    additionalContext,
    pluginName,
    delayBeforeCallingAIService,
    getTailorContext,
  } = options;
  const { userid, username } = botServiceConfig;
  const isTroubleshooting = !isCustomStream && category === CATEGORY_TROUBLESHOOTING;

  const from = { id: userid, name: username };
  const value = createMessageValue({
    locale,
    botServiceConfig,
    isHiddenFromUser,
    isTroubleshooting,
    options: additionalOptions,
    additionalContext,
    pluginName,
    delayBeforeCallingAIService,
    getTailorContext,
  });

  let message: Message = {
    type: 'message',
    from,
    text,
    value,
  };

  if (isTroubleshooting) {
    // send out troubleshooting trigger event
    message = _.defaults({
      type: 'event',
      name: EVENT_NAME_ENGAGE_TROUBLESHOOTER,
      params: {
        promptText: message.text,
        promptType: TROUBLESHOOTING_AD_DELIVERY,
      },
    }, message);
  }

  return message;
}
